import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { withdrawInvitation as withdrawInvitationApi } from "../api/invitations";
import { useHostToast } from "../../host/useHostToast";
import { useTrack } from "../../host/useTrack";

const MY_TEAM = "My Team";

interface UseWithdrawInvitation {
  pending: boolean;
  /** invitedEmail is forwarded as the tracking label — pass it in so
   *  the hook stays decoupled from invitation lookups. */
  withdraw: (id: string | number, invitedEmail?: string) => Promise<boolean>;
}

// Tracking events mirror WithdrawInvitation.vue.
export function useWithdrawInvitation(): UseWithdrawInvitation {
  const { t } = useTranslation();
  const toast = useHostToast();
  const track = useTrack();
  const [pending, setPending] = useState(false);

  const withdraw = useCallback(
    async (id: string | number, invitedEmail?: string) => {
      setPending(true);
      try {
        await withdrawInvitationApi(id);
        track("Team Management > Cancel pending invitation > Cancel succeeded", {
          eventCategory: MY_TEAM,
          eventLabel: invitedEmail,
        });
        toast.success(t("team.toast.invitationWithdrawn"));
        return true;
      } catch {
        track("Team Management > Cancel pending invitation > Cancel failed", {
          eventCategory: MY_TEAM,
        });
        toast.error(t("team.toast.genericError"));
        return false;
      } finally {
        setPending(false);
      }
    },
    [t, toast, track],
  );

  return { withdraw, pending };
}
